import { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { field, money } from "./BudgetPanel";

const API = process.env.REACT_APP_BACKEND_URL;
const NEW = "__new__";
const today = () => new Date().toISOString().slice(0, 10);
const defaultDate = (month) => (today().startsWith(month) ? today() : `${month}-01`);

function SpendForm({ token, options, initial, month, onDone, onCancel }) {
  const [form, setForm] = useState(initial || { date: defaultDate(month), amount: "", key: options[0] || NEW, source: "", campaign: "", reference: "" });
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const [source, campaign] = form.key === NEW ? [form.source, form.campaign] : form.key.split("|");
    const r = await fetch(`${API}/api/marketing/stats/spend${initial ? `/${initial.id}` : ""}`, {
      method: initial ? "PUT" : "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ date: form.date, amount: Number(form.amount), source, campaign, reference: form.reference }),
    }).catch(() => null);
    if (!r?.ok) return setError("Could not save this entry. Check the date, amount and campaign.");
    onDone();
  };

  const pfx = initial ? "edit-spend" : "add-spend";
  return (
    <form onSubmit={submit} className="grid grid-cols-2 lg:grid-cols-6 gap-2 items-end" data-testid={`${pfx}-form`}>
      <input type="date" className={field} value={form.date} onChange={set("date")} required aria-label="Date" data-testid={`${pfx}-date`} />
      <input type="number" min="0.01" step="0.01" inputMode="decimal" className={field} placeholder="Amount ($)" value={form.amount} onChange={set("amount")} required aria-label="Amount" data-testid={`${pfx}-amount`} />
      <select className={`${field} col-span-2`} value={form.key} onChange={set("key")} aria-label="Campaign" data-testid={`${pfx}-campaign-select`}>
        {options.map((o) => <option key={o} value={o}>{o.replace("|", " · ")}</option>)}
        <option value={NEW}>New campaign…</option>
      </select>
      {form.key === NEW && (
        <>
          <input className={field} placeholder="Source" value={form.source} onChange={set("source")} required aria-label="New campaign source" data-testid={`${pfx}-new-source`} />
          <input className={field} placeholder="Campaign" value={form.campaign} onChange={set("campaign")} required aria-label="New campaign name" data-testid={`${pfx}-new-campaign`} />
        </>
      )}
      <input className={`${field} col-span-2 lg:col-span-1`} placeholder="Receipt / reference (optional)" value={form.reference} onChange={set("reference")} maxLength={200} aria-label="Reference" data-testid={`${pfx}-reference`} />
      <div className="col-span-2 lg:col-span-1 flex gap-2">
        <button type="submit" data-testid={`${pfx}-submit`} className="px-4 py-2 rounded-full bg-[#E8712C] hover:bg-[#C55A1F] text-white text-[14px] font-semibold transition-colors">
          {initial ? "Save" : "Add spending"}
        </button>
        {onCancel && <button type="button" onClick={onCancel} data-testid={`${pfx}-cancel`} className="px-3 py-2 rounded-full text-[14px] text-[#5B6B7B] hover:bg-[#F4F6F8]">Cancel</button>}
      </div>
      {error && <p className="col-span-2 lg:col-span-6 text-[13px] text-[#B42318]" role="alert">{error}</p>}
    </form>
  );
}

export default function SpendEntries({ token, month, rows, entries, onChange }) {
  const [linkKeys, setLinkKeys] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formKey, setFormKey] = useState(0);

  useEffect(() => {
    fetch(`${API}/api/marketing/stats/links`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { links: [] }))
      .then((d) => setLinkKeys((d.links || []).map((l) => `${l.source}|${l.campaign}`)))
      .catch(() => {});
  }, [token]);

  const options = [...new Set([...rows.map((r) => `${r.source}|${r.campaign}`), ...linkKeys])];

  const remove = async (id) => {
    if (!window.confirm("Delete this spending entry?")) return;
    const r = await fetch(`${API}/api/marketing/stats/spend/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }).catch(() => null);
    if (r?.ok) onChange();
  };

  return (
    <div className="border-t border-[#EEE9E0] pt-5 space-y-4" data-testid="spend-entries">
      <h3 className="text-[14px] font-semibold text-[#1A2A3A]">Record spending</h3>
      <SpendForm key={`${month}-${formKey}`} token={token} options={options} month={month} onDone={() => { setFormKey((k) => k + 1); onChange(); }} />
      <ul className="divide-y divide-[#EEE9E0]" data-testid="spend-entries-list">
        {entries.length === 0 && <li className="py-3 text-[13px] text-[#5B6B7B]">No spending recorded this month.</li>}
        {entries.map((e) =>
          editing === e.id ? (
            <li key={e.id} className="py-3">
              <SpendForm token={token} options={options}
                initial={{ id: e.id, date: e.date, amount: e.amount, key: `${e.source}|${e.campaign}`, source: "", campaign: "", reference: e.reference || "" }}
                onDone={() => { setEditing(null); onChange(); }} onCancel={() => setEditing(null)} />
            </li>
          ) : (
            <li key={e.id} className="py-3 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`spend-entry-${e.id}`}>
              <span className="text-[13px] text-[#5B6B7B] tabular-nums w-24">{e.date}</span>
              <span className="text-[14px] font-semibold tabular-nums w-24">{money(e.amount)}</span>
              <span className="text-[14px] flex-1 min-w-[140px]">{e.source} · {e.campaign}</span>
              {e.reference && <span className="text-[12px] text-[#5B6B7B] break-all">{e.reference}</span>}
              <span className="flex gap-1">
                <button onClick={() => setEditing(e.id)} aria-label="Edit entry" data-testid={`spend-entry-edit-${e.id}`} className="p-2 rounded-full text-[#5B6B7B] hover:text-[#334E68] hover:bg-[#F4F6F8]"><Pencil size={14} /></button>
                <button onClick={() => remove(e.id)} aria-label="Delete entry" data-testid={`spend-entry-delete-${e.id}`} className="p-2 rounded-full text-[#5B6B7B] hover:text-[#B42318] hover:bg-[#B42318]/5"><Trash2 size={14} /></button>
              </span>
            </li>
          )
        )}
      </ul>
    </div>
  );
}
